import React, { useEffect, useState, useMemo } from "react";
import { Modal, Slide, Stack, Typography, Box, Button, Select, InputLabel, MenuItem, Grid, Autocomplete, IconButton } from "@mui/material";
import ReactPerfectScrollbar from "react-perfect-scrollbar";

import { ReactComponent as CloseIcon } from "../../../../../vendor/_new/icon/navbar/notification/close_big.svg";
import { ReactComponent as InfoIcon } from "../../../../../vendor/_new/icon/sites/info.svg";
import { ReactComponent as ExceptIcon } from "../../../../../vendor/_new/icon/services/except.svg";
import { ReactComponent as ArrowdownIcon } from "../../../../../vendor/_new/icon/sites/arrow_down.svg";
import { ReactComponent as DuplicateIcon } from "../../../../../vendor/_new/icon/services/duplicate.svg";
import { ReactComponent as DeleteIcon } from "../../../../../vendor/_new/icon/services/delete.svg";
import { ReactComponent as PlusIcon } from "../../../../../vendor/_new/icon/sites/plus.svg";

import { SafeTextField, GreenSelectForm, GreenDateTimeLocalField } from "../../../../common/Greens";
import { ExpressionKeyField, FireWallExpressionCondition, UserType } from "../../../../../utils/constants";
import { getExpressionKeyTitle } from "../../../../../utils/constants";
import { FirewallAction, FwMode, ChallengeKind } from "../../../../../utils/constants";
import { getConditionTitle, getValidCondFlag } from "../../../application/rule/component";
import countries from "../../../application/rule/countries";
import colors from "../../../../../utils/colors";
import useAuth from "../../../../../hooks/useAuth";
import useFirewall from "../../../../../hooks/user/useFirewall";
import { useNotification } from "../../../../../contexts/NotificationContext";
import { validateExpressionRuleForm, validateExpiresAt } from "../../../../../utils/validation";
import {
  getExpressionValueHint,
  isExpressionRowStarted,
  resolveVisibleCellError,
} from "../../../../../utils/validation/expressionValidators";
import { containedButtonSx, SaveButton, formSaveButtonSx } from "../../../../common/Button";
import FormHelpTooltip from "../../../../common/FormHelpTooltip";
import { ExpressionFieldMessage } from "../../../../common/ExpressionFieldMessages";
import { useDrawerBaselineDirty, useGuardedDrawerClose } from "../../super/design";

const menuItemSx = {
  height: 38,
  fontSize: 14,
  lineHeight: "21px",
  "&:hover": { color: colors.primary[300] },
};

const KeyComponent = ({ keyStr, selectKeyStr, padx, pady, advanced, error }) => {
  const aKeys = [
    ExpressionKeyField.SOURCE_IP,
    ExpressionKeyField.HOST_NAME,
    ExpressionKeyField.USER_AGENT,
    ExpressionKeyField.HEADER,
    ExpressionKeyField.QUERY,
    ExpressionKeyField.COOKIE,
    ExpressionKeyField.URI,
    ExpressionKeyField.REFERER,
    ExpressionKeyField.METHOD,
  ];
  const aAdvancedKeys = [
    ExpressionKeyField.COUNTRY,
    ExpressionKeyField.CITY_NAME,
    ExpressionKeyField.AS_NUMBER,
    ExpressionKeyField.BOT_SCORE,
    ExpressionKeyField.JA3_FINGERPRINT,
  ];
  return (
    <GreenSelectForm error={error}>
      <InputLabel id="field-information">Field</InputLabel>
      <Select labelId="field-information" fullWidth IconComponent={ArrowdownIcon} value={keyStr} onChange={selectKeyStr}>
        {keyStr === ExpressionKeyField.NONE && (
          <MenuItem
            key={`_key0_${padx}_${pady}`}
            value={ExpressionKeyField.NONE}
            disabled
            sx={{ ...menuItemSx, display: "none!important" }}
          >
            Select Field ...
          </MenuItem>
        )}
        {aKeys.map((keyValue, keyIdx) => (
          <MenuItem key={`_key${keyIdx + 1}_${padx}_${pady}`} value={keyValue} sx={menuItemSx}>
            {getExpressionKeyTitle(keyValue)}
          </MenuItem>
        ))}
        {(undefined === advanced || true === advanced) &&
          aAdvancedKeys.map((keyValue, keyIdx) => (
            <MenuItem key={`_key${keyIdx + 1 + aKeys.length}_${padx}_${pady}`} value={keyValue} sx={menuItemSx}>
              {getExpressionKeyTitle(keyValue)}
            </MenuItem>
          ))}
      </Select>
    </GreenSelectForm>
  );
};

const CondComponent = ({ condStr, selectCondStr, disableMenu, disabled, padx, pady, error }) => {
  // Order should NOT be modified
  const aConditions = [
    FireWallExpressionCondition.EQUALS,
    FireWallExpressionCondition.NOT_EQUALS,
    FireWallExpressionCondition.CONTAINS,
    FireWallExpressionCondition.NOT_CONTAINS,
    FireWallExpressionCondition.GREATER_THAN,
    FireWallExpressionCondition.LESS_THAN,
    FireWallExpressionCondition.GREATER_THAN_OR_EQUALS_TO,
    FireWallExpressionCondition.LESS_THAN_OR_EQUALS_TO,
  ];
  // switch
  return (
    <GreenSelectForm error={error}>
      <InputLabel id="operation-information">Operator</InputLabel>
      <Select
        fullWidth
        labelId="operation-information"
        IconComponent={ArrowdownIcon}
        disabled={disabled}
        value={condStr}
        onChange={selectCondStr}
      >
        {condStr === "none" && (
          <MenuItem key={"_cond0_" + padx + "_" + pady} value="none" disabled sx={{ ...menuItemSx, display: "none!important" }}>
            Select Item ...
          </MenuItem>
        )}
        {aConditions
          .filter((_, condIdx) => disableMenu[condIdx])
          .map((condValue, condIdx) => (
            <MenuItem key={`_cond${condIdx + 1}_${padx}_${pady}`} value={condValue} sx={menuItemSx}>
              {getConditionTitle(condValue)}
            </MenuItem>
          ))}
      </Select>
    </GreenSelectForm>
  );
};

const MethodValueComponent = ({ valueStr, selectValueStr, disabled, padx, pady, error }) => {
  const aMethods = ["GET", "POST", "HEAD", "PUT", "DELETE", "PATCH", "TRACE", "CONNECT"];
  return (
    <GreenSelectForm error={error}>
      <InputLabel id="method-value-information">Value</InputLabel>
      <Select
        fullWidth
        labelId="method-value-information"
        IconComponent={ArrowdownIcon}
        disabled={disabled}
        value={valueStr}
        onChange={selectValueStr}
      >
        {valueStr === "none" && (
          <MenuItem key={"_cond0_" + padx + "_" + pady} value="none" disabled sx={{ ...menuItemSx, display: "none!important" }}>
            Select Item ...
          </MenuItem>
        )}
        {aMethods.map((methodValue, methodIdx) => (
          <MenuItem key={`_cond${methodIdx + 1}_${padx}_${pady}`} value={methodValue} sx={{ menuItemSx }}>
            {methodValue}
          </MenuItem>
        ))}
      </Select>
    </GreenSelectForm>
  );
};
const CountryValueComponent = ({ valueStr, selectValueStr, disabled, error }) => {
  const getName = (code) => {
    let label;
    countries?.forEach((country) => {
      if (country?.value === code) label = country?.label;
    });
    return label;
  };
  return (
    <Autocomplete
      fullWidth
      options={countries}
      sx={{ border: `solid 1px ${error ? colors.red[500] : colors.neutral[200]}`, borderRadius: "8px" }}
      autoHighlight
      disabled={disabled}
      onChange={selectValueStr}
      value={getName(valueStr)}
      renderOption={(props, option) => (
        <Box
          component="option"
          value={option.value}
          {...props}
          sx={ { fontSize: 14, lineHeight: "21px", color: colors.neutral[1000] }}
        >
          {option.label} ({option.value})
        </Box>
      )}
      renderInput={(params) => (
        <SafeTextField
          {...params}
          placeholder="Select a country"
          sx={ { fontSize: 14, lineHeight: "21px", color: colors.neutral[1000] }}
        />
      )}
    />
  );
};
const ValueComponent = ({ keyStr, valueStr, selectValueStr, onBlur, disabled, padx, pady, error, helperText }) => {
  let isNumber = false;
  switch (keyStr) {
    case ExpressionKeyField.AS_NUMBER:
    case ExpressionKeyField.BOT_SCORE:
      isNumber = true;
      break;
    default:
      isNumber = false;
      break;
  }
  const type = isNumber ? "number" : "text";
  return (
    <SafeTextField
      label="Value"
      fullWidth
      type={type}
      value={valueStr}
      disabled={disabled}
      onChange={selectValueStr}
      onBlur={onBlur}
      key={`_value_${padx}_${pady}`}
      error={Boolean(error)}
      helperText={error || helperText || ""}
    />
  );
};

export const FirewallRuleModal = ({
  siteUid,
  open,
  handleClose,
  firewallToEdit,
  prefilled = null,
  canWriteFirewallForSite = true,
}) => {
  const { isAuthenticated, userType } = useAuth();
  const { updateFirewall, addNewFirewall, getCurrentFirewall, setErr, errMsg, getFirewalls } = useFirewall();
  const [firewallName, setFirewallName] = useState("");
  const [conditions, setConditions] = useState([[{ key: "none", condition: "none", value: "" }]]);

  const [action, setAction] = useState(FirewallAction.LOG);
  const [mode, setMode] = useState(FwMode.ENFORCE);
  const [challengeKind, setChallengeKind] = useState(ChallengeKind.MANAGED);
  const [expiresAt, setExpiresAt] = useState("");
  const [disabledUntil, setDisabledUntil] = useState("");
  const [disableReason, setDisableReason] = useState("");
  const [expiresAtError, setExpiresAtError] = useState("");

  const [nameError, setNameError] = useState(false);
  const [conditionErrors, setConditionErrors] = useState([[{ key: false, condition: false, value: false }]]);
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [cellTouched, setCellTouched] = useState({});
  const [baselineKey, setBaselineKey] = useState(0);

  const touchCell = (i, j, field) => {
    setCellTouched((prev) => ({ ...prev, [`${i}.${j}.${field}`]: true }));
  };

  const isCellTouched = (i, j, field) => Boolean(cellTouched[`${i}.${j}.${field}`]);

  const showCellError = (i, j, field, errorMsg, andCond) =>
    resolveVisibleCellError(errorMsg, {
      touched: isCellTouched(i, j, field),
      submitAttempted,
      rowStarted: isExpressionRowStarted(andCond),
      valueEntered: field === "value" && String(andCond?.value ?? "").trim() !== "",
    });

  const formValidation = useMemo(
    () => validateExpressionRuleForm({ name: firewallName, conditions }),
    [firewallName, conditions],
  );

  const showNameErr = submitAttempted && formValidation.formErrors.name;
  const showConditionsErr = submitAttempted && formValidation.formErrors.conditions;

  // ISO datetimes from the server arrive in "Z" format; <input type="datetime-local"> needs "YYYY-MM-DDTHH:MM".
  const toLocalInput = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    if (isNaN(d.getTime())) return "";
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  };
  const toIsoOrNull = (local) => {
    if (!local) return null;
    const d = new Date(local);
    if (isNaN(d.getTime())) return null;
    return d.toISOString();
  };

  useEffect(() => {
    if (!open) return undefined;
    window.scrollTo(0, 0);
    const init = async () => {
      if (firewallToEdit) {
        const detailedRule = await getCurrentFirewall(siteUid, firewallToEdit?.id);
        setFirewallName(detailedRule?.name ? detailedRule?.name : undefined);
        const conds = detailedRule?.conditions ? detailedRule?.conditions : [[{ key: "none", condition: "none", value: "" }]];
        setConditions(conds);
        setConditionErrors(conds.map((orCond) => orCond.map(() => ({ key: false, condition: false, value: false }))));
        setAction(detailedRule?.action ? detailedRule?.action : FirewallAction.LOG);
        setMode(detailedRule?.mode ?? FwMode.ENFORCE);
        setChallengeKind(detailedRule?.challenge_kind ?? ChallengeKind.MANAGED);
        setExpiresAt(toLocalInput(detailedRule?.expires_at));
        setDisabledUntil(toLocalInput(detailedRule?.disabled_until));
        setDisableReason(detailedRule?.disable_reason ?? "");
      } else if (prefilled) {
        setFirewallName(prefilled.name || "");
        const conds = prefilled.conditions || [[{ key: "none", condition: "none", value: "" }]];
        setConditions(conds);
        setConditionErrors(conds.map((orCond) => orCond.map(() => ({ key: false, condition: false, value: false }))));
        setAction(prefilled.action ?? FirewallAction.LOG);
        setMode(FwMode.ENFORCE);
        setChallengeKind(ChallengeKind.MANAGED);
        setExpiresAt("");
        setDisabledUntil("");
        setDisableReason("");
      } else {
        setFirewallName("");
        setConditions([[{ key: "none", condition: "none", value: "" }]]);
        setConditionErrors([[{ key: false, condition: false, value: false }]]);
        setAction(FirewallAction.LOG);
        setMode(FwMode.ENFORCE);
        setChallengeKind(ChallengeKind.MANAGED);
        setExpiresAt("");
        setDisabledUntil("");
        setDisableReason("");
      }
      setNameError(false);
      setSubmitAttempted(false);
      setCellTouched({});
      setBaselineKey((k) => k + 1);
    };
    if (isAuthenticated) init();
    return () => setErr(null);
  }, [firewallToEdit, prefilled, open, getCurrentFirewall, isAuthenticated, siteUid, setErr]);

  const firewallNameChange = (event) => {
    setFirewallName(event.target.value);
    if (event.target.value.trim() !== "") setNameError(false);
  };

  const selectKeyStr = (event, i, j) => {
    let list = [...conditions];
    list[i][j]["key"] = event.target.value;
    setConditions(list);
    let errList = [...conditionErrors];
    if (errList[i] && errList[i][j]) {
      errList[i][j].key = false;
      setConditionErrors(errList);
    }
  };
  const selectCondStr = (event, i, j) => {
    let list = [...conditions];
    list[i][j]["condition"] = event.target.value;
    setConditions(list);
    let errList = [...conditionErrors];
    if (errList[i] && errList[i][j]) {
      errList[i][j].condition = false;
      setConditionErrors(errList);
    }
  };
  const selectValueStr = (event, i, j) => {
    let list = [...conditions];
    list[i][j]["value"] = event.target.value;
    setConditions(list);
    let errList = [...conditionErrors];
    if (errList[i] && errList[i][j]) {
      errList[i][j].value = false;
      setConditionErrors(errList);
    }
  };
  const selectCountryStr = (event, newValue, i, j) => {
    let list = [...conditions];
    list[i][j]["value"] = newValue?.value;
    setConditions(list);
    let errList = [...conditionErrors];
    if (errList[i] && errList[i][j]) {
      errList[i][j].value = false;
      setConditionErrors(errList);
    }
  };

  const andClick = (i, j) => {
    let list = [...conditions];
    list[i].splice(j + 1, 0, { key: "none", condition: "none", value: "" });
    setConditions(list);
    let errList = [...conditionErrors];
    if (errList[i]) {
      errList[i].splice(j + 1, 0, { key: false, condition: false, value: false });
      setConditionErrors(errList);
    }
  };

  const orClick = () => {
    setConditions([...conditions, [{ key: "none", condition: "none", value: "" }]]);
    setConditionErrors([...conditionErrors, [{ key: false, condition: false, value: false }]]);
  };

  const removeClick = (i, j) => {
    let list = [...conditions];
    list[i].splice(j, 1);
    if (list[i].length === 0) list.splice(i, 1);
    if (0 === list.length) {
      list.push([{ key: "none", condition: "none", value: "" }]);
    }
    setConditions(list);
    let errList = [...conditionErrors];
    if (errList[i]) {
      errList[i].splice(j, 1);
      if (errList[i].length === 0) errList.splice(i, 1);
      if (0 === errList.length) {
        errList.push([{ key: false, condition: false, value: false }]);
      }
      setConditionErrors(errList);
    }
  };

  const { showSuccess, showError } = useNotification();
  const [loading, setLoading] = React.useState(false);

  const formSnapshot = useMemo(
    () => ({
      firewallName,
      conditions,
      action,
      mode,
      challengeKind,
      expiresAt,
      disabledUntil,
      disableReason,
    }),
    [firewallName, conditions, action, mode, challengeKind, expiresAt, disabledUntil, disableReason],
  );
  const isDirty = useDrawerBaselineDirty(open, formSnapshot, baselineKey);
  const { requestClose, confirmDialog } = useGuardedDrawerClose(handleClose, isDirty, {
    disabled: loading,
  });

  const handleSave = async () => {
    if (!canWriteFirewallForSite) return;
    if (action === FirewallAction.BYPASS) {
      showError("Action 'BYPASS' is panel-only and not supported by the data plane");
      return;
    }
    setSubmitAttempted(true);
    const validation = validateExpressionRuleForm({ name: firewallName, conditions });
    const expiresValidation = validateExpiresAt(expiresAt);
    setExpiresAtError(expiresValidation.valid ? "" : expiresValidation.message);
    if (!validation.valid || !expiresValidation.valid) {
      setConditionErrors(validation.conditionErrors);
      return;
    }
    setLoading(true);
    try {
      const body = {
        name: firewallName,
        conditions: conditions,
        action,
        mode,
        expires_at: toIsoOrNull(expiresAt),
        disabled_until: toIsoOrNull(disabledUntil),
        disable_reason: disableReason || null,
      };
      if (action === FirewallAction.CHALLENGE) {
        body.challenge_kind = challengeKind;
      }
      let result = {};
      if (firewallToEdit) {
        result = await updateFirewall(siteUid, firewallToEdit?.id, body);
      } else {
        result = await addNewFirewall(siteUid, body);
      }
      if (result?.status === "success") {
        showSuccess(result?.msg || "Firewall rule saved successfully");
        getFirewalls(siteUid, false);
        handleClose();
      } else {
        showError(result?.msg || "Failed to save firewall rule");
      }
    } catch (err) {
      showError(err?.message || "Failed to save firewall rule");
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    if (errMsg) {
      showError(errMsg);
    }
  }, [errMsg, showError]);

  return (
    <>
      <Modal 
        open={open} 
        onClose={requestClose}
        slotProps={{
          backdrop: {
            sx: { backgroundColor: 'rgba(0, 0, 0, 0.5)' }
          }
        }}
      >
        <Slide direction="left" in={open} mountOnEnter unmountOnExit>
          <Stack
            sx={{
              position: "fixed",
              top: 0,
              right: 0,
              width: "33%",
              minWidth: 480,
              height: "100vh",
              backgroundColor: "#fff",
              boxShadow: "-4px 0 20px rgba(0,0,0,0.1)",
              zIndex: 1300,
              borderTopLeftRadius: "8px",
            }}
          >
            <Stack direction={"row"} justifyContent={"space-between"} alignItems={"flex-start"} spacing={6} sx={{ px: 4, pt: 4, pb: 3 }}>
              <Stack direction="row" alignItems="center" spacing={2}>
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: "8px",
                    backgroundColor: "#EEF0FE",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <ExceptIcon style={{ width: 32, height: 32, color: "#6371F7" }} />
                </Box>
                <Stack>
                  <Stack direction="row" alignItems="center" sx={{ flexWrap: "nowrap" }}>
                    <Typography
                      sx={ { fontWeight: "medium", fontSize: 20, lineHeight: "30px", color: colors.neutral[1000] }}
                    >
                      {firewallToEdit ? "Edit firewall" : "Add firewall"}
                    </Typography>
                    <FormHelpTooltip tooltip="Create a firewall rule by IP, country, ASN, or expression. Rules are evaluated before WAF and can allow, block, challenge, or log matching requests." />
                  </Stack>
                  <Typography sx={ { fontSize: 12, lineHeight: "18px", color: colors.neutral[700] }}>
                    Set up firewall rules to protect your application from threats.
                  </Typography>
                </Stack>
              </Stack>
              <IconButton onClick={requestClose} aria-label="Close" sx={{ p: 0 }}>
                <CloseIcon style={{ color: colors.neutral[600] }} />
              </IconButton>
            </Stack>
            <Box sx={{ py: 4, pl: 4, height: `calc(100vh - 130px)` }}>
              <ReactPerfectScrollbar>
                <Stack justifyContent={"space-between"} spacing={4} sx={{ pr: 4, height: "100%" }}>
                  <Stack spacing={4}>
                    <SafeTextField fullWidth label="Firewall name" value={firewallName} onChange={firewallNameChange} error={Boolean(showNameErr)} helperText={showNameErr || ""} />
                    {conditions.map((orCond, i) => {
                      return (
                        <Box
                          key={`cond_${i}`}
                          sx={{ width: "100%", p: 4, borderRadius: "8px", border: `solid 1px ${colors.neutral[200]}` }}
                        >
                          <Grid container spacing={3}>
                            {orCond.map((andCond, j) => {
                              const cellErr = formValidation.conditionErrors[i]?.[j] || {};
                              const keyErr = showCellError(i, j, "key", cellErr.key, andCond);
                              const condErr = showCellError(i, j, "condition", cellErr.condition, andCond);
                              const valueErr = showCellError(i, j, "value", cellErr.value, andCond);
                              const valueHint = getExpressionValueHint(andCond.key);
                              return (
                                <React.Fragment key={`andCond_${i}_${j}`}>
                                  <Grid item xs={12}>
                                    <Typography sx={ { fontSize: 16, lineHeight: "24px", color: colors.neutral[1000] }}>
                                      {j === 0 ? (i === 0 ? "When incoming requests match..." : "Or") : "And"}
                                    </Typography>
                                  </Grid>
                                  <Grid item xs={12}>
                                    <Grid container spacing={3}>
                                      <Grid item xs={12} sm={6}>
                                        <Box onBlur={() => touchCell(i, j, "key")}>
                                          <KeyComponent
                                            keyStr={andCond.key}
                                            selectKeyStr={(event) => {
                                              touchCell(i, j, "key");
                                              selectKeyStr(event, i, j);
                                            }}
                                            padx={i}
                                            pady={j}
                                            error={keyErr}
                                          />
                                          <ExpressionFieldMessage error={keyErr} />
                                        </Box>
                                      </Grid>
                                      <Grid item xs={12} sm={6}>
                                        <Box onBlur={() => touchCell(i, j, "condition")}>
                                          <CondComponent
                                            condStr={andCond.condition}
                                            selectCondStr={(event) => {
                                              touchCell(i, j, "condition");
                                              selectCondStr(event, i, j);
                                            }}
                                            disableMenu={getValidCondFlag(andCond.key)}
                                            disabled={andCond.key === "none"}
                                            padx={i}
                                            pady={j}
                                            error={condErr}
                                          />
                                          <ExpressionFieldMessage error={condErr} />
                                        </Box>
                                      </Grid>
                                      <Grid item xs={12}>
                                        {andCond.key === ExpressionKeyField.METHOD ? (
                                          <Box onBlur={() => touchCell(i, j, "value")}>
                                            <MethodValueComponent
                                              valueStr={andCond.value}
                                              selectValueStr={(event) => {
                                                touchCell(i, j, "value");
                                                selectValueStr(event, i, j);
                                              }}
                                              disabled={andCond.key === ExpressionKeyField.NONE || andCond.condition === "none"}
                                              padx={i}
                                              pady={j}
                                              error={valueErr}
                                            />
                                            <ExpressionFieldMessage error={valueErr} hint={valueHint} />
                                          </Box>
                                        ) : andCond.key === ExpressionKeyField.COUNTRY ? (
                                          <Box onBlur={() => touchCell(i, j, "value")}>
                                            <CountryValueComponent
                                              valueStr={andCond.value}
                                              selectValueStr={(event, newValue) => {
                                                touchCell(i, j, "value");
                                                selectCountryStr(event, newValue, i, j);
                                              }}
                                              disabled={andCond.key === "none" || andCond.condition === "none"}
                                              error={valueErr}
                                            />
                                            <ExpressionFieldMessage error={valueErr} hint={valueHint} />
                                          </Box>
                                        ) : (
                                          <ValueComponent
                                            keyStr={andCond.key}
                                            valueStr={andCond.value}
                                            selectValueStr={(event) => {
                                              touchCell(i, j, "value");
                                              selectValueStr(event, i, j);
                                            }}
                                            onBlur={() => touchCell(i, j, "value")}
                                            disabled={andCond.key === ExpressionKeyField.NONE || andCond.condition === "none"}
                                            padx={i}
                                            pady={j}
                                            error={valueErr}
                                            helperText={valueHint}
                                          />
                                        )}
                                      </Grid>
                                      <Grid item xs={12}>
                                        <Stack width={"100%"} alignItems={"center"} direction="row" justifyContent="end" spacing={4}>
                                          <Stack
                                            direction={"row"}
                                            alignItems={"center"}
                                            spacing={1}
                                            onClick={() => andClick(i, j)}
                                            sx={{
                                              cursor: "pointer",
                                              borderRadius: "8px",
                                              border: `solid 1px ${colors.secondary[300]}`,
                                              px: 2,
                                              py: 1,
                                            }}
                                          >
                                            <DuplicateIcon style={{ color: colors.neutral[800] }} />
                                            <Typography
                                              sx={ { fontSize: 14, lineHeight: "21px", color: colors.neutral[800] }}
                                            >
                                              And
                                            </Typography>
                                          </Stack>
                                          <Box onClick={() => removeClick(i, j)} sx={{ cursor: "pointer" }}>
                                            <DeleteIcon style={{ color: colors.neutral[600] }} />
                                          </Box>
                                        </Stack>
                                      </Grid>
                                      {conditions.length - 1 === i && orCond.length - 1 === j && (
                                        <Grid item xs={12}>
                                          <Button
                                            startIcon={<PlusIcon style={{ color: colors.neutral[100] }} />}
                                            onClick={orClick}
                                            sx={{
                                              height: 32,
                                              width: 105,
                                              ...containedButtonSx,
                                            }}
                                          >
                                            Add new
                                          </Button>
                                        </Grid>
                                      )}
                                    </Grid>
                                  </Grid>
                                </React.Fragment>
                              );
                            })}
                          </Grid>
                        </Box>
                      );
                    })}
                    {showConditionsErr && (
                      <Typography sx={{ fontSize: 12, color: "#d32f2f", mt: -2 }}>
                        {formValidation.formErrors.conditions}
                      </Typography>
                    )}
                    <GreenSelectForm variant="outlined" fullWidth>
                      <InputLabel id="waf-engine-information">Firewall Engine Selection</InputLabel>
                      <Select
                        fullwidth
                        IconComponent={ArrowdownIcon}
                        labelId="waf-engine-information"
                        value={action}
                        onChange={(event) => {
                          setAction(event.target.value);
                        }}
                      >
                        <MenuItem key={"action_0"} value={FirewallAction.LOG} sx={menuItemSx}>
                          Log
                        </MenuItem>
                        <MenuItem key={"action_2"} value={FirewallAction.ALLOW} sx={menuItemSx}>
                          Allow
                        </MenuItem>
                        <MenuItem key={"action_3"} value={FirewallAction.CHALLENGE} sx={menuItemSx}>
                          Challenge
                        </MenuItem>
                        <MenuItem key={"action_4"} value={FirewallAction.BLOCK} sx={menuItemSx}>
                          Block
                        </MenuItem>
                        <MenuItem key={"action_5"} value={FirewallAction.DROP} sx={menuItemSx}>
                          Drop
                        </MenuItem>
                      </Select>
                    </GreenSelectForm>
                    <GreenSelectForm variant="outlined" fullWidth>
                      <InputLabel id="fw-mode-label">Mode</InputLabel>
                      <Select
                        fullwidth
                        IconComponent={ArrowdownIcon}
                        labelId="fw-mode-label"
                        value={mode}
                        onChange={(e) => setMode(e.target.value)}
                      >
                        <MenuItem value={FwMode.OFF} sx={menuItemSx}>Off</MenuItem>
                        <MenuItem value={FwMode.ENFORCE} sx={menuItemSx}>Enforce</MenuItem>
                        <MenuItem value={FwMode.SHADOW} sx={menuItemSx}>Shadow</MenuItem>
                      </Select>
                    </GreenSelectForm>
                    {action === FirewallAction.CHALLENGE && (
                      <GreenSelectForm variant="outlined" fullWidth>
                        <InputLabel id="challenge-kind-label">Challenge kind</InputLabel>
                        <Select
                          fullwidth
                          IconComponent={ArrowdownIcon}
                          labelId="challenge-kind-label"
                          value={challengeKind}
                          onChange={(e) => setChallengeKind(e.target.value)}
                        >
                          <MenuItem value={ChallengeKind.INVISIBLE} sx={menuItemSx}>Invisible</MenuItem>
                          <MenuItem value={ChallengeKind.MANAGED} sx={menuItemSx}>Managed</MenuItem>
                          <MenuItem value={ChallengeKind.ADAPTIVE} sx={menuItemSx}>Adaptive</MenuItem>
                          <MenuItem value={ChallengeKind.INTERACTIVE} sx={menuItemSx}>Interactive</MenuItem>
                        </Select>
                      </GreenSelectForm>
                    )}
                    <GreenDateTimeLocalField
                      label="Expires at"
                      fullWidth
                      disablePast
                      value={expiresAt}
                      error={Boolean(expiresAtError)}
                      helperText={expiresAtError || ""}
                      onChange={(e) => {
                        setExpiresAt(e.target.value);
                        setExpiresAtError("");
                      }}
                    />
                    <GreenDateTimeLocalField
                      label="Disabled until"
                      fullWidth
                      value={disabledUntil}
                      onChange={(e) => setDisabledUntil(e.target.value)}
                    />
                    <SafeTextField
                      label="Disable reason"
                      fullWidth
                      value={disableReason}
                      onChange={(e) => setDisableReason(e.target.value)}
                    />
                    <Stack spacing={4}>
                      <Stack
                        direction={"row"}
                        spacing={2}
                        sx={{
                          minHeight: 48,
                          p: 2,
                          borderRadius: "8px",
                          backgroundColor: colors.primary["alpha"],
                          border: `solid 1px ${colors.primary[300]}`,
                        }}
                      >
                        <Box sx={{ width: 24, height: 24 }}>
                          <InfoIcon style={{ color: colors.primary[300], width: 24, height: 24 }} />
                        </Box>
                        <Typography sx={ { fontSize: 14, lineHeight: "21px", color: colors.primary[300] }}>
                          You can update, review, or manage settings on this screen. Changes will be applied instantly after save!
                        </Typography>
                      </Stack>
                    </Stack>
                  </Stack>
                  <Stack direction={"row"} alignItems={"center"} spacing={3}>
                    <SaveButton
                      onClick={handleSave}
                      disabled={loading || !canWriteFirewallForSite}
                      loading={loading}
                      label="Apply"
                      sx={formSaveButtonSx}
                    />
                    <Button
                      variant="outlined"
                      onClick={requestClose}
                      sx={{
                        height: 44,
                        width: "50%",
                        borderRadius: "8px",
                        fontSize: 14,
                        lineHeight: "21px",
                        color: colors.primary[300],
                        fontWeight: "regular",
                        textTransform: "none",
                        border: `solid 1px ${colors.primary[300]}`,
                        "&:hover": {
                          color: colors.primary[400],
                          borderColor: colors.primary[400],
                          backgroundColor: "transparent",
                        },
                      }}
                    >
                      Cancel
                    </Button>
                  </Stack>
                </Stack>
              </ReactPerfectScrollbar>
            </Box>
          </Stack>
        </Slide>
      </Modal>
      {confirmDialog}
    </>
  );
};
export default FirewallRuleModal;
